import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  MAP_CONFIG,
  info,
  topDownSVG,
  STATUS_HEX,
  STATUS_LABEL,
  sliceRoute,
  findFractionOnRoute,
  ic,
  dt,
  OFF_HEX,
  TRIP_HEX,
  VIO,
  vioValue,
  clock,
  dur,
  tripOf,
  lastTripOf,
  placeShort,
  placeName,
  FUEL_CONFIG,
  bearing,
  hav,
  ago,
} from './trackingUtils';

const CLUSTER_PX = 48;
const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

function boundsOf(pts) {
  if (!pts || !pts.length) return [[66, 24], [74, 34]];
  let minLng = 180,
    maxLng = -180,
    minLat = 90,
    maxLat = -90;
  pts.forEach((p) => {
    if (!p) return;
    minLng = Math.min(minLng, p[0]);
    maxLng = Math.max(maxLng, p[0]);
    minLat = Math.min(minLat, p[1]);
    maxLat = Math.max(maxLat, p[1]);
  });
  if (minLng === maxLng && minLat === maxLat) {
    return [
      [minLng - 0.01, minLat - 0.01],
      [maxLng + 0.01, maxLat + 0.01],
    ];
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

const VehicleTrackingMap = ({
  mode = 'live', // 'live' | 'history'
  vehicles = [],
  selectedVehicle = null,
  onSelectVehicle,
  followVehicle = false,
  onToggleFollow,
  flyTrigger = null,
  triggerFitTrip = null,
  mapOnly = false,
  onToggleMapOnly,
  // History Mode Props
  focusedVehicle = null,
  historyData = null,
  fleetHistoryData = [],
  hoveredReg = null,
  onHoverReg,
  playbackStep = 0,
  onSeekPlayback,
  isPlaying = false,
  onTogglePlay,
  playbackSpeed = 4,
  onChangePlaybackSpeed,
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const overlayPinsRef = useRef([]);
  const lastOverlayRegRef = useRef(null);
  const histMarkersRef = useRef([]);
  const carMarkerRef = useRef(null);
  const initialFitDoneRef = useRef(false);
  const lastFlownTriggerRef = useRef(null);
  const lastFlownRegRef = useRef(null);
  const lastTargetCoordsRef = useRef({});
  const savedOverviewViewRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const activeBasemap = 'esri';
  const [groupedMarkers, setGroupedMarkers] = useState(() => {
    try {
      return localStorage.getItem('fms.mapGrouped') === '1';
    } catch {
      return false;
    }
  });
  const clusterMarkersRef = useRef([]);
  const clusterPopupRef = useRef(null);
  const [toastInfo, setToastInfo] = useState(null);

  useEffect(() => {
    if (!toastInfo) return;
    const timer = setTimeout(() => {
      setToastInfo(null);
    }, 6500);
    return () => clearTimeout(timer);
  }, [toastInfo]);

  useEffect(() => {
    let map = null;

    const checkAndInit = () => {
      const maplibregl = window.maplibregl;
      if (!maplibregl || !mapContainerRef.current) return;

      const prov = MAP_CONFIG.providers[activeBasemap] || MAP_CONFIG.providers.osm;

      const style = {
        version: 8,
        sources: {
          osm: prov.tiles
            ? {
                type: 'raster',
                tiles: prov.tiles,
                tileSize: prov.tileSize,
                maxzoom: prov.maxzoom,
                attribution: prov.attribution,
              }
            : null,
        },
        layers: [
          { id: 'bg', type: 'background', paint: { 'background-color': '#e9edf2' } },
          ...(prov.tiles
            ? [
                {
                  id: 'osm-layer',
                  type: 'raster',
                  source: 'osm',
                  paint: {
                    'raster-opacity': 1,
                    'raster-fade-duration': 100,
                  },
                },
              ]
            : []),
        ].filter(Boolean),
      };

      try {
        map = new maplibregl.Map({
          container: mapContainerRef.current,
          style,
          center: [69.5, 28.8],
          zoom: 5.5,
          maxZoom: 18,
          maxBounds: [
            [55.0, 20.0],
            [84.0, 40.0],
          ],
          dragRotate: false,
          pitchWithRotate: false,
          attributionControl: false,
          refreshExpiredTiles: false,
        });

        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

        map.on('load', () => {
          mapInstanceRef.current = map;
          setMapLoaded(true);
        });
      } catch (err) {
        console.error('Failed to initialize MapLibre map:', err);
      }
    };

    if (window.maplibregl) {
      checkAndInit();
    } else {
      const interval = setInterval(() => {
        if (window.maplibregl) {
          clearInterval(interval);
          checkAndInit();
        }
      }, 200);
      return () => clearInterval(interval);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run map constructor only once on mount!

  // Dynamically swap basemap raster tiles without tearing down map or resetting zoom
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded) return;

    const prov = MAP_CONFIG.providers[activeBasemap] || MAP_CONFIG.providers.osm;
    if (map.getLayer('osm-layer')) map.removeLayer('osm-layer');
    if (map.getSource('osm')) map.removeSource('osm');

    if (prov.tiles) {
      map.addSource('osm', {
        type: 'raster',
        tiles: prov.tiles,
        tileSize: prov.tileSize,
        maxzoom: prov.maxzoom,
        attribution: prov.attribution,
      });

      const styleLayers = map.getStyle()?.layers || [];
      const firstNonBg = styleLayers.find((l) => l.id !== 'bg')?.id;
      map.addLayer(
        {
          id: 'osm-layer',
          type: 'raster',
          source: 'osm',
          paint: {
            'raster-opacity': 1,
            'raster-fade-duration': 100,
          },
        },
        firstNonBg
      );
    }
  }, [activeBasemap, mapLoaded]);

  // Clean helper for layer & source
  const safeRemoveLayer = (map, id) => {
    if (map.getLayer(id)) map.removeLayer(id);
    if (map.getSource(id)) map.removeSource(id);
  };

  const openClusterList = useCallback(
    (map, vs, at) => {
      if (clusterPopupRef.current) {
        clusterPopupRef.current.remove();
        clusterPopupRef.current = null;
      }

      const maplibregl = window.maplibregl;
      if (!maplibregl || !map) return;

      const rows = vs
        .map((v) => {
          const i = info(v);
          const col = STATUS_HEX[i.display] || '#999999';
          const drvName = v.drv || v.driver || '—';
          const vType = v.type || v.vehicle_type || 'Vehicle';
          const lastSeen = ago(v.last || v.last_updated);
          const cat = v.cat || 'HTV';
          return `
            <div class="cl-row" data-v="${esc(v.reg)}">
              <span class="sdot" style="width:8px;height:8px;border-radius:50%;background:${col};flex:none;display:inline-block"></span>
              <span class="who">
                <b>${esc(v.reg)} <span class="cat ${cat}">${cat}</span></b>
                <small>${esc(drvName)} · ${esc(vType)} · ${esc(lastSeen)}</small>
              </span>
              <span class="badge b-${i.display} pill" style="font-size:11px;margin-left:auto">
                <span class="dot" style="background:${col}"></span>${STATUS_LABEL[i.display] || i.display}
              </span>
            </div>
          `;
        })
        .join('');

      let atLoc = '';
      const firstWithLoc = vs.find((v) => v.location || v.start_trip?.location);
      if (firstWithLoc) {
        atLoc = firstWithLoc.location || firstWithLoc.start_trip?.location;
      } else {
        atLoc = placeName(at) || `${at[1].toFixed(4)}, ${at[0].toFixed(4)}`;
      }

      const container = document.createElement('div');
      container.className = 'cl-pop';
      container.innerHTML = `
        <div class="pop-h">
          <b>${vs.length} vehicles at this location</b>
        </div>
        <div class="muted" style="padding:6px 12px 0;font-size:12px">${esc(atLoc)}</div>
        <div class="cl-list">${rows}</div>
      `;

      container.querySelectorAll('.cl-row').forEach((row) => {
        row.addEventListener('click', (e) => {
          e.stopPropagation();
          const reg = row.dataset.v;
          const found = vs.find((x) => x.reg === reg);
          if (found) {
            if (clusterPopupRef.current) {
              clusterPopupRef.current.remove();
              clusterPopupRef.current = null;
            }
            onSelectVehicle && onSelectVehicle(found);
          }
        });
      });

      const popup = new maplibregl.Popup({
        offset: 20,
        maxWidth: '340px',
        closeOnClick: true,
        className: 'cl-cluster-popup',
      })
        .setLngLat(at)
        .setDOMContent(container)
        .addTo(map);

      clusterPopupRef.current = popup;
    },
    [onSelectVehicle]
  );

  const clusterVehicles = useCallback(
    (map, list, opts = {}) => {
      if (!map || !map.getContainer()) return;

      clusterMarkersRef.current.forEach((m) => m.remove());
      clusterMarkersRef.current = [];

      const maplibregl = window.maplibregl;
      if (!maplibregl) return;

      const currentMarkers = markersRef.current;

      const showMarker = (m) => {
        if (!m) return;
        const el = m.getElement();
        if (el) {
          el.classList.remove('vt-hidden');
          el.removeAttribute('data-hidden');
          el.style.display = '';
          el.style.visibility = '';
        }
      };

      const hideMarker = (m) => {
        if (!m) return;
        const el = m.getElement();
        if (el) {
          el.classList.add('vt-hidden');
          el.setAttribute('data-hidden', 'true');
          el.style.setProperty('display', 'none', 'important');
          el.style.setProperty('visibility', 'hidden', 'important');
        }
      };

      // If grouping is turned off, show all individual vehicle markers
      if (!groupedMarkers) {
        Object.values(currentMarkers).forEach(showMarker);
        return;
      }

      // Project real vehicle coordinates to screen pixel points
      const pts = (list || [])
        .filter((v) => v.reg !== opts.sel)
        .map((v) => {
          const i = info(v);
          if (!i || !i.p) return null;
          try {
            const px = map.project(i.p);
            return { v, i, x: px.x, y: px.y };
          } catch {
            return null;
          }
        })
        .filter(Boolean);

      const used = new Set();
      const groups = [];
      const rank = { alert: 0, moving: 1, idle: 2, stopped: 3, offline: 4, inactive: 5 };
      pts.sort(
        (a, b) =>
          (rank[a.i.display] ?? 99) - (rank[b.i.display] ?? 99) ||
          a.v.reg.localeCompare(b.v.reg)
      );

      for (const a of pts) {
        if (used.has(a.v.reg)) continue;
        const g = [a];
        used.add(a.v.reg);
        for (const b of pts) {
          if (!used.has(b.v.reg) && Math.hypot(a.x - b.x, a.y - b.y) < CLUSTER_PX) {
            g.push(b);
            used.add(b.v.reg);
          }
        }
        groups.push(g);
      }

      // Default: ensure all individual markers are visible first
      Object.values(currentMarkers).forEach(showMarker);

      for (const g of groups) {
        if (g.length < 2) continue; // Single vehicles remain individual markers

        // Completely hide individual markers for vehicles in this cluster
        g.forEach((q) => {
          hideMarker(currentMarkers[q.v.reg]);
        });

        const lng = g.reduce((s, q) => s + q.i.p[0], 0) / g.length;
        const lat = g.reduce((s, q) => s + q.i.p[1], 0) / g.length;

        const counts = {};
        g.forEach((q) => {
          const st = q.i.display || 'offline';
          counts[st] = (counts[st] || 0) + 1;
        });

        const el = document.createElement('div');
        const order = ['moving', 'idle', 'stopped', 'offline', 'inactive', 'alert'];
        let acc = 0;
        const stops = order
          .filter((st) => counts[st])
          .map((st) => {
            const a0 = (acc / g.length) * 360;
            acc += counts[st];
            const a1 = (acc / g.length) * 360;
            const color = STATUS_HEX[st] || '#999999';
            return `${color} ${a0.toFixed(1)}deg ${a1.toFixed(1)}deg`;
          })
          .join(', ');

        const size = g.length >= 10 ? 38 : 34;
        el.className = 'vc';
        el.title = `${g.length} vehicles · ` + order
          .filter((st) => counts[st])
          .map((st) => `${STATUS_LABEL[st] || st} ${counts[st]}`)
          .join(', ');
        el.setAttribute('aria-label', el.title);
        el.innerHTML = `<span class="pie" style="width:${size}px;height:${size}px;background:conic-gradient(${stops})"><span class="hole">${g.length}</span></span>`;

        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          let spread = 0;
          for (const a of g) {
            for (const b of g) {
              spread = Math.max(spread, hav(a.i.p, b.i.p));
            }
          }
          if (spread < 40 || map.getZoom() >= 15.5) {
            openClusterList(map, g.map((q) => q.v), [lng, lat]);
          } else {
            map.fitBounds(boundsOf(g.map((q) => q.i.p)), { padding: 90, maxZoom: 16, duration: 700 });
          }
        });

        const cMarker = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
        clusterMarkersRef.current.push(cMarker);
      }
    },
    [groupedMarkers, openClusterList]
  );

  const handleToggleGrouping = () => {
    const next = !groupedMarkers;
    setGroupedMarkers(next);
    try {
      localStorage.setItem('fms.mapGrouped', next ? '1' : '0');
    } catch {
      // ignore
    }
    setToastInfo({
      message: next
        ? 'Nearby vehicles are <b>grouped</b> into count circles.'
        : 'Showing <b>every vehicle individually</b>. Zoom in where they overlap.',
    });
  };

  // ----------------------------------------------------
  // 1. LIVE MODE: Update vehicle markers & active routes
  // ----------------------------------------------------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || !window.maplibregl) return;

    // If in history mode, clear live markers
    if (mode === 'history') {
      Object.keys(markersRef.current).forEach((reg) => {
        markersRef.current[reg].remove();
        delete markersRef.current[reg];
      });
      clusterMarkersRef.current.forEach((m) => m.remove());
      clusterMarkersRef.current = [];
      if (clusterPopupRef.current) {
        clusterPopupRef.current.remove();
        clusterPopupRef.current = null;
      }
      safeRemoveLayer(map, 'trip-left');
      safeRemoveLayer(map, 'trip-done');
      overlayPinsRef.current.forEach((m) => m.remove());
      overlayPinsRef.current = [];
      return;
    }

    const maplibregl = window.maplibregl;
    const currentMarkers = markersRef.current;
    const activeRegs = new Set(vehicles.map((v) => v.reg));
    if (selectedVehicle?.reg) {
      activeRegs.add(selectedVehicle.reg);
    }

    // Include selectedVehicle so it is never dropped from map even if filtered
    const vehiclesToRender = [...vehicles];
    if (selectedVehicle && !vehiclesToRender.some((v) => v.reg === selectedVehicle.reg)) {
      vehiclesToRender.push(selectedVehicle);
    }

    // Clear stale history markers if switched from history mode
    if (histMarkersRef.current && histMarkersRef.current.length > 0) {
      histMarkersRef.current.forEach((m) => m.remove());
      histMarkersRef.current = [];
    }
    safeRemoveLayer(map, 'routes');
    safeRemoveLayer(map, 'hi');
    safeRemoveLayer(map, 'track');
    safeRemoveLayer(map, 'offtrip');
    safeRemoveLayer(map, 'played');
    if (carMarkerRef.current) {
      carMarkerRef.current.remove();
      carMarkerRef.current = null;
    }

    // Remove deleted markers
    Object.keys(currentMarkers).forEach((reg) => {
      if (!activeRegs.has(reg)) {
        if (currentMarkers[reg]._animId) {
          cancelAnimationFrame(currentMarkers[reg]._animId);
        }
        currentMarkers[reg].remove();
        delete currentMarkers[reg];
      }
    });

    // Add or update live vehicle markers
    vehiclesToRender.forEach((v) => {
      try {
        const isSel = selectedVehicle?.reg === v.reg;
        const targetVehicle = isSel && selectedVehicle ? selectedVehicle : v;
        const i = info(targetVehicle);

        // If vehicle has no valid coordinates, clean up any stale marker and skip
        if (!i || !i.p) {
          if (currentMarkers[v.reg]) {
            if (currentMarkers[v.reg]._animId) {
              cancelAnimationFrame(currentMarkers[v.reg]._animId);
            }
            currentMarkers[v.reg].remove();
            delete currentMarkers[v.reg];
          }
          return;
        }

        const col = STATUS_HEX[i.display] || '#5C5F80';
        const heading = Math.round(i.h || 0);

        if (currentMarkers[v.reg]) {
          const marker = currentMarkers[v.reg];
          if (marker._animId) {
            cancelAnimationFrame(marker._animId);
            marker._animId = null;
          }
          // Set coordinates directly — guarantees marker stays 100% geographically pinned to GPS location
          marker.setLngLat(i.p);
          lastTargetCoordsRef.current[v.reg] = i.p;

          const wrap = marker.getElement();
          const el = wrap.querySelector('.vm') || wrap;
          el.classList.toggle('sel', isSel);
          wrap.style.zIndex = isSel ? '60' : '5';
          const tdv = el.querySelector('.tdv');
          if (tdv) {
            tdv.style.transform = `rotate(${heading}deg)`;
            tdv.innerHTML = topDownSVG(targetVehicle.cat, col);
          }
          const tag = el.querySelector('.tag');
          if (tag) {
            if (map.getZoom() >= 8.5 || isSel) {
              tag.classList.add('on');
            } else {
              tag.classList.remove('on');
            }
          }
        } else {
          const wrap = document.createElement('div');
          wrap.className = 'vt-marker-wrap';
          wrap.style.zIndex = isSel ? '60' : '5';

          const el = document.createElement('div');
          el.className = `vm ${targetVehicle.cat} ${isSel ? 'sel' : ''}`;
          el.title = `${targetVehicle.reg} (${targetVehicle.type}) - ${STATUS_LABEL[i.display]}`;

          el.innerHTML = `
            <span class="tdv" style="transform: rotate(${heading}deg);">
              ${topDownSVG(targetVehicle.cat, col)}
            </span>
            <span class="tag ${map.getZoom() >= 8.5 || isSel ? 'on' : ''}">${targetVehicle.reg}</span>
          `;

          el.addEventListener('click', (e) => {
            e.stopPropagation();
            if (selectedVehicle?.reg === v.reg) {
              onSelectVehicle && onSelectVehicle(null);
            } else {
              onSelectVehicle && onSelectVehicle(v);
            }
          });

          wrap.appendChild(el);
          const marker = new maplibregl.Marker({ element: wrap, anchor: 'center' }).setLngLat(i.p).addTo(map);
          currentMarkers[v.reg] = marker;
        }
      } catch (markerErr) {
        console.error(`Error adding marker for vehicle ${v.reg}:`, markerErr);
      }
    });

    // Perform dynamic overlap clustering based on current zoom/pan pixel distance
    clusterVehicles(map, vehiclesToRender, { sel: selectedVehicle?.reg });

    // Update tags ONLY when zoom finishes — NEVER loop and thrash DOM during interactive zoom frames!
    const handleZoomEnd = () => {
      const z = map.getZoom();
      const showAll = z >= 8.5;
      Object.keys(currentMarkers).forEach((reg) => {
        const wrap = currentMarkers[reg]?.getElement();
        const tag = wrap?.querySelector('.tag');
        if (tag) {
          if (showAll || selectedVehicle?.reg === reg) {
            tag.classList.add('on');
          } else {
            tag.classList.remove('on');
          }
        }
      });
      clusterVehicles(map, vehiclesToRender, { sel: selectedVehicle?.reg });
    };
    map.on('zoomend', handleZoomEnd);

    // Selected vehicle route overlay
    if (!selectedVehicle) {
      safeRemoveLayer(map, 'trip-casing');
      safeRemoveLayer(map, 'trip-left');
      safeRemoveLayer(map, 'trip-done');
      safeRemoveLayer(map, 'trip-last');
      overlayPinsRef.current.forEach((m) => m.remove());
      overlayPinsRef.current = [];
      lastOverlayRegRef.current = null;
    } else {
      const v = selectedVehicle;
      const i = info(v);
      const T = tripOf(v);
      let route = null;
      let tripA = null;
      let tripB = null;
      let fromLabel = '';
      let toLabel = '';
      let frac = 0;

      if (v.polylineGeoJson && v.polylineGeoJson.length >= 2) {
        route = v.polylineGeoJson;
        const originLng = Number(v.start_trip?.longitude);
        const originLat = Number(v.start_trip?.latitude);
        const destLng = Number(v.end_trip?.longitude);
        const destLat = Number(v.end_trip?.latitude);

        tripA = !isNaN(originLng) && !isNaN(originLat) && originLng !== 0 && originLat !== 0
          ? [originLng, originLat]
          : route[0];
        tripB = !isNaN(destLng) && !isNaN(destLat) && destLng !== 0 && destLat !== 0
          ? [destLng, destLat]
          : route[route.length - 1];
        fromLabel = v.start_trip?.location || 'Origin';
        toLabel = v.end_trip?.location || 'Destination';
      } else if (T) {
        route = T.route;
        tripA = T.a;
        tripB = T.b;
        fromLabel = T.from;
        toLabel = T.to;
      } else {
        const originLng = Number(v.start_trip?.longitude);
        const originLat = Number(v.start_trip?.latitude);
        const destLng = Number(v.end_trip?.longitude);
        const destLat = Number(v.end_trip?.latitude);

        if (!isNaN(originLng) && !isNaN(originLat) && !isNaN(destLng) && !isNaN(destLat) && originLng !== 0 && destLng !== 0) {
          route = [[originLng, originLat], ...(i.p ? [i.p] : []), [destLng, destLat]];
          tripA = [originLng, originLat];
          tripB = [destLng, destLat];
          fromLabel = v.start_trip?.location || 'Origin';
          toLabel = v.end_trip?.location || 'Destination';
        } else {
          const L = lastTripOf(v);
          if (L && L.line && L.line.length >= 2) {
            route = L.line;
            tripA = L.a;
            tripB = L.b;
            fromLabel = L.from;
            toLabel = L.to;
          } else if (v.route && v.route.length >= 2) {
            route = v.route;
            tripA = route[0];
            tripB = route[route.length - 1];
            fromLabel = 'Origin';
            toLabel = 'Destination';
          }
        }
      }

      if (route && route.length >= 2) {
        const isCompleted = v.st === 'completed' || v.trip_status === 'completed' || v.trip?.status === 'completed';
        if (isCompleted) {
          frac = 1;
        } else if (i && i.p) {
          frac = findFractionOnRoute(route, i.p);
        } else if (T?.frac != null) {
          frac = T.frac;
        } else if (v.t != null) {
          frac = Number(v.t);
        } else {
          frac = 0.5;
        }

        let doneCoords = null;
        let leftCoords = null;

        if (frac >= 0.999) {
          doneCoords = [...route];
        } else if (frac <= 0.001) {
          doneCoords = [route[0]];
          leftCoords = [...route];
        } else {
          doneCoords = sliceRoute(route, 0, frac);
          leftCoords = sliceRoute(route, frac, 1);
        }

        // CRUCIAL: Seamlessly attach the solid traveled line directly into the live vehicle coordinates (i.p)
        if (i && i.p && Array.isArray(i.p) && i.p.length === 2 && !isNaN(i.p[0]) && !isNaN(i.p[1])) {
          if (!doneCoords || doneCoords.length === 0) {
            doneCoords = [i.p];
          } else {
            const lastDone = doneCoords[doneCoords.length - 1];
            if (!lastDone || Math.abs(lastDone[0] - i.p[0]) > 0.00001 || Math.abs(lastDone[1] - i.p[1]) > 0.00001) {
              doneCoords = [...doneCoords, i.p];
            }
          }

          if (leftCoords && leftCoords.length > 0) {
            const firstLeft = leftCoords[0];
            if (!firstLeft || Math.abs(firstLeft[0] - i.p[0]) > 0.00001 || Math.abs(firstLeft[1] - i.p[1]) > 0.00001) {
              leftCoords = [i.p, ...leftCoords];
            }
          }
        }

        const casingCoords = leftCoords && leftCoords.length >= 2
          ? [...doneCoords, ...leftCoords.slice(1)]
          : doneCoords;

        // Smooth GPU update via .setData() without tearing down layers!
        if (map.getSource('trip-casing')) {
          map.getSource('trip-casing').setData({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: casingCoords },
          });
        } else {
          map.addSource('trip-casing', {
            type: 'geojson',
            data: { type: 'Feature', geometry: { type: 'LineString', coordinates: casingCoords } },
          });
          map.addLayer({
            id: 'trip-casing',
            type: 'line',
            source: 'trip-casing',
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': '#ffffff',
              'line-width': ['interpolate', ['linear'], ['zoom'], 4, 6, 8, 8, 12, 10, 16, 12],
              'line-opacity': 0.8,
            },
          });
        }

        if (doneCoords && doneCoords.length >= 2) {
          if (map.getSource('trip-done')) {
            map.getSource('trip-done').setData({
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: doneCoords },
            });
          } else {
            map.addSource('trip-done', {
              type: 'geojson',
              data: { type: 'Feature', geometry: { type: 'LineString', coordinates: doneCoords } },
            });
            map.addLayer({
              id: 'trip-done',
              type: 'line',
              source: 'trip-done',
              layout: { 'line-cap': 'round', 'line-join': 'round' },
              paint: {
                'line-color': '#4014B8',
                'line-width': ['interpolate', ['linear'], ['zoom'], 4, 3.5, 8, 5, 12, 6.5, 16, 8],
              },
            });
          }
        } else {
          safeRemoveLayer(map, 'trip-done');
        }

        if (leftCoords && leftCoords.length >= 2) {
          if (map.getSource('trip-left')) {
            map.getSource('trip-left').setData({
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: leftCoords },
            });
          } else {
            map.addSource('trip-left', {
              type: 'geojson',
              data: { type: 'Feature', geometry: { type: 'LineString', coordinates: leftCoords } },
            });
            map.addLayer({
              id: 'trip-left',
              type: 'line',
              source: 'trip-left',
              layout: { 'line-cap': 'round', 'line-join': 'round' },
              paint: {
                'line-color': '#7F67D2',
                'line-width': ['interpolate', ['linear'], ['zoom'], 4, 3, 8, 4.5, 12, 5.5, 16, 7],
                'line-dasharray': [2, 2],
                'line-opacity': 0.95,
              },
            });
          }
        } else {
          safeRemoveLayer(map, 'trip-left');
        }

        // Align selected vehicle marker rotation to exact road segment of travel
        if (currentMarkers[v.reg]) {
          let segH = null;
          if (doneCoords && doneCoords.length >= 2) {
            segH = Math.round(bearing(doneCoords[doneCoords.length - 2], doneCoords[doneCoords.length - 1]));
          } else if (leftCoords && leftCoords.length >= 2) {
            segH = Math.round(bearing(leftCoords[0], leftCoords[1]));
          }
          if (segH !== null) {
            const el = currentMarkers[v.reg].getElement();
            const tdv = el.querySelector('.tdv');
            if (tdv) {
              tdv.style.transform = `rotate(${segH}deg)`;
            }
          }
        }

        // Only recreate origin/destination/stop pins if selected vehicle registration changed
        const isNewSelection = lastOverlayRegRef.current !== v.reg;
        if (isNewSelection) {
          lastOverlayRegRef.current = v.reg;
          overlayPinsRef.current.forEach((m) => m.remove());
          overlayPinsRef.current = [];

          // Origin pin A
          if (tripA) {
            const origPinEl = document.createElement('div');
            origPinEl.className = 'trip-pin a';
            origPinEl.title = `Origin · ${fromLabel}`;
            origPinEl.innerHTML = `<span>A · ${placeShort(fromLabel)}</span>`;
            const pA = new maplibregl.Marker({ element: origPinEl, anchor: 'center' })
              .setLngLat(tripA)
              .addTo(map);
            overlayPinsRef.current.push(pA);
          }

          // Destination pin B
          if (tripB) {
            const destPinEl = document.createElement('div');
            destPinEl.className = 'trip-pin b';
            destPinEl.title = `Destination · ${toLabel}`;
            destPinEl.innerHTML = `<span>B · ${placeShort(toLabel)}</span>`;
            const pB = new maplibregl.Marker({ element: destPinEl, anchor: 'bottom-left' })
              .setLngLat(tripB)
              .addTo(map);
            overlayPinsRef.current.push(pB);
          }

          // Intermediate Stop pins if available
          if (Array.isArray(v.stops)) {
            v.stops.forEach((stop, sIdx) => {
              const sLng = Number(stop.longitude);
              const sLat = Number(stop.latitude);
              if (!isNaN(sLng) && !isNaN(sLat) && sLng !== 0 && sLat !== 0) {
                const stopPinEl = document.createElement('div');
                const isLoad = String(stop.stop_type || '').toLowerCase() === 'load';
                stopPinEl.className = `trip-pin stop ${isLoad ? 'stop-load' : 'stop-unload'}`;
                stopPinEl.title = `${isLoad ? 'Load' : 'Unload'} Stop #${stop.stop_number || sIdx + 1} · ${stop.location || ''}`;
                stopPinEl.style.cssText = `
                  background: ${isLoad ? '#2563EB' : '#DC2626'};
                  color: #ffffff;
                  font-size: 10px;
                  font-weight: 700;
                  padding: 2px 6px;
                  border-radius: 4px;
                  border: 1.5px solid #ffffff;
                  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                  white-space: nowrap;
                  cursor: pointer;
                `;
                stopPinEl.innerHTML = `<span>S${stop.stop_number || sIdx + 1}</span>`;
                const pStop = new maplibregl.Marker({ element: stopPinEl, anchor: 'center' })
                  .setLngLat([sLng, sLat])
                  .addTo(map);
                overlayPinsRef.current.push(pStop);
              }
            });
          }
        }
      }

      if (followVehicle && i && i.p) {
        map.easeTo({ center: i.p, duration: 900 });
      }
    }

    const handleUserDragStart = () => {
      if (followVehicle && onToggleFollow) {
        onToggleFollow(false);
      }
    };
    map.on('dragstart', handleUserDragStart);

    return () => {
      map.off('zoomend', handleZoomEnd);
      map.off('dragstart', handleUserDragStart);
    };
  }, [
    mode,
    vehicles,
    selectedVehicle,
    followVehicle,
    mapLoaded,
    onSelectVehicle,
    onToggleFollow,
    groupedMarkers,
    clusterVehicles,
  ]);

  // When user turns followVehicle back ON, instantly glide back to vehicle center
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || mode !== 'live' || !selectedVehicle || !followVehicle) return;

    const i = info(selectedVehicle);
    if (!i || !i.p) return;

    map.easeTo({
      center: i.p,
      duration: 700,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followVehicle, selectedVehicle?.reg, mapLoaded, mode]);

  // Fly / pan to vehicle in live mode when selected, and return back to previous map view when closed
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || mode !== 'live') return;

    if (!selectedVehicle) {
      if (lastFlownRegRef.current) {
        lastFlownRegRef.current = null;
        if (savedOverviewViewRef.current) {
          map.easeTo({
            center: savedOverviewViewRef.current.center,
            zoom: savedOverviewViewRef.current.zoom,
            pitch: savedOverviewViewRef.current.pitch || 0,
            bearing: savedOverviewViewRef.current.bearing || 0,
            duration: 700,
          });
          savedOverviewViewRef.current = null;
        } else if (vehicles && vehicles.length > 0) {
          const allPts = vehicles
            .map((v) => info(v).p)
            .filter((p) => p && Array.isArray(p) && p.length === 2 && !isNaN(p[0]) && !isNaN(p[1]));
          if (allPts.length) {
            const b = boundsOf(allPts);
            map.fitBounds(b, {
              padding: { top: 70, bottom: 70, left: mapOnly ? 70 : 380, right: 70 },
              maxZoom: 11,
              duration: 700,
            });
          }
        }
      }
      return;
    }

    const isNewVehicle = selectedVehicle.reg !== lastFlownRegRef.current;
    const isExplicitTrigger = flyTrigger && flyTrigger !== lastFlownTriggerRef.current;

    // Do NOT re-center if user is just panning or if this is a background data tick
    if (!isNewVehicle && !isExplicitTrigger) return;

    // Capture the current map position before zooming into the vehicle so we can restore on close
    if (!lastFlownRegRef.current) {
      savedOverviewViewRef.current = {
        center: map.getCenter(),
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
      };
    }

    lastFlownRegRef.current = selectedVehicle.reg;
    lastFlownTriggerRef.current = flyTrigger;

    const i = info(selectedVehicle);
    if (!i || !i.p) return;

    map.easeTo({
      center: i.p,
      zoom: 13.5,
      duration: 700,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle?.reg, flyTrigger, mapLoaded, mode, vehicles, mapOnly]);

  // Deselect vehicle when clicking on empty map background
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || mode !== 'live') return;

    const handleMapBackgroundClick = () => {
      if (selectedVehicle && onSelectVehicle) {
        onSelectVehicle(null);
      }
    };

    map.on('click', handleMapBackgroundClick);
    return () => {
      map.off('click', handleMapBackgroundClick);
    };
  }, [mapLoaded, mode, selectedVehicle, onSelectVehicle]);

  // Handle fleet overview on initial mount ONLY (never fight user dragging/panning the map)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || mode !== 'live') return;

    if (!selectedVehicle) {
      // Clear route overlay and pins
      safeRemoveLayer(map, 'trip-casing');
      safeRemoveLayer(map, 'trip-left');
      safeRemoveLayer(map, 'trip-done');
      safeRemoveLayer(map, 'trip-last');
      overlayPinsRef.current.forEach((m) => m.remove());
      overlayPinsRef.current = [];

      // ONLY fit bounds once when valid vehicle coordinates exist! Never auto-center while user freely moves the map
      if (!initialFitDoneRef.current && vehicles && vehicles.length > 0) {
        const allPts = vehicles
          .map((v) => info(v).p)
          .filter((p) => p && Array.isArray(p) && p.length === 2 && !isNaN(p[0]) && !isNaN(p[1]));
        if (allPts.length) {
          const b = boundsOf(allPts);
          map.fitBounds(b, {
            padding: { top: 70, bottom: 70, left: mapOnly ? 70 : 380, right: 70 },
            maxZoom: 11,
            duration: 800,
          });
          initialFitDoneRef.current = true;
        }
      }
    }
  }, [selectedVehicle, mapLoaded, mode, vehicles, mapOnly]);

  // Fit full trip on map when triggerFitTrip is updated
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || !selectedVehicle || !triggerFitTrip) return;

    const v = selectedVehicle;
    const T = tripOf(v);
    const startLng = Number(v.start_trip?.longitude);
    const startLat = Number(v.start_trip?.latitude);
    const endLng = Number(v.end_trip?.longitude);
    const endLat = Number(v.end_trip?.latitude);
    const fallbackRoute =
      !isNaN(startLng) && !isNaN(startLat) && !isNaN(endLng) && !isNaN(endLat) && startLng !== 0 && endLng !== 0
        ? [[startLng, startLat], ...(info(v).p ? [info(v).p] : []), [endLng, endLat]]
        : null;
    const route = v.polylineGeoJson || (T ? T.route : v.route) || fallbackRoute;
    if (route && route.length >= 2) {
      const b = boundsOf(route);
      map.fitBounds(b, {
        padding: { top: 90, bottom: 90, left: mapOnly ? 90 : 380, right: 90 },
        maxZoom: 13,
        duration: 800,
      });
    }
  }, [triggerFitTrip, selectedVehicle, mapLoaded, mapOnly]);

  // Smoothly resize MapLibre canvas when entering/exiting fullscreen or container resizing
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded) return;
    const t1 = setTimeout(() => map.resize(), 60);
    const t2 = setTimeout(() => map.resize(), 180);
    const t3 = setTimeout(() => map.resize(), 350);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [mapOnly, mapLoaded]);

  // Observe container size changes (mobile layout changes, rotation, etc.)
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const ro = new ResizeObserver(() => {
      if (mapInstanceRef.current && mapLoaded) {
        mapInstanceRef.current.resize();
      }
    });
    ro.observe(mapContainerRef.current);
    return () => ro.disconnect();
  }, [mapLoaded]);

  // ----------------------------------------------------
  // 2. HISTORY MODE - FLEET OVERVIEW (when !focusedVehicle)
  // ----------------------------------------------------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || mode !== 'history' || focusedVehicle) return;

    const maplibregl = window.maplibregl;
    histMarkersRef.current.forEach((m) => m.remove());
    histMarkersRef.current = [];
    clusterMarkersRef.current.forEach((m) => m.remove());
    clusterMarkersRef.current = [];
    if (clusterPopupRef.current) {
      clusterPopupRef.current.remove();
      clusterPopupRef.current = null;
    }

    safeRemoveLayer(map, 'routes');
    safeRemoveLayer(map, 'hi');
    safeRemoveLayer(map, 'track');
    safeRemoveLayer(map, 'offtrip');
    safeRemoveLayer(map, 'played');
    if (carMarkerRef.current) {
      carMarkerRef.current.remove();
      carMarkerRef.current = null;
    }

    if (!fleetHistoryData.length) return;

    const fc = (hiReg) => ({
      type: 'FeatureCollection',
      features: fleetHistoryData
        .filter((d) => !hiReg || d.v.reg === hiReg)
        .map((d) => ({
          type: 'Feature',
          properties: { reg: d.v.reg },
          geometry: {
            type: 'LineString',
            coordinates: (d.H?.pts || []).map((q) => q.p),
          },
        })),
    });

    map.addSource('routes', { type: 'geojson', data: fc() });
    map.addLayer({
      id: 'routes',
      type: 'line',
      source: 'routes',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#9B87E8',
        'line-width': 3,
        'line-opacity': 0.9,
      },
    });

    map.addSource('hi', { type: 'geojson', data: fc('__none') });
    map.addLayer({
      id: 'hi',
      type: 'line',
      source: 'hi',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#4014B8', 'line-width': 5 },
    });

    // Add end position markers for each vehicle
    fleetHistoryData.forEach((d) => {
      const pts = d.H?.pts || [];
      if (!pts.length) return;
      const q = pts[pts.length - 1];
      const st = q.spd > 0 ? 'moving' : q.ign ? 'idle' : 'stopped';
      const col = STATUS_HEX[st] || '#5C5F80';
      const heading = Math.round(q.h || 0);

      const el = document.createElement('div');
      el.className = `vm ${d.v.cat}`;
      el.title = `${d.v.reg} · ${Math.round(d.km)} km · ended ${clock(d.end)}`;
      el.style.cursor = 'pointer';

      el.innerHTML = `
        <span class="tdv" style="transform: rotate(${heading}deg);">
          ${topDownSVG(d.v.cat, col)}
        </span>
        <span class="tag on">${d.v.reg}</span>
      `;

      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        onSelectVehicle && onSelectVehicle(d.v);
      });

      el.addEventListener('mouseenter', () => onHoverReg && onHoverReg(d.v.reg));
      el.addEventListener('mouseleave', () => onHoverReg && onHoverReg(null));

      const marker = new maplibregl.Marker({ element: el }).setLngLat(d.endP).addTo(map);
      histMarkersRef.current.push(marker);
    });

    // Fit bounds to fleet
    const allEndPts = fleetHistoryData.flatMap((d) => [d.H?.pts?.[0]?.p, d.endP]).filter(Boolean);
    if (allEndPts.length) {
      map.fitBounds(boundsOf(allEndPts), { padding: 50, duration: 600 });
    }
  }, [mode, focusedVehicle, fleetHistoryData, mapLoaded, onSelectVehicle, onHoverReg]);

  // Update hover line for fleet history
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || mode !== 'history' || focusedVehicle) return;

    if (map.getSource('hi')) {
      const hiData = {
        type: 'FeatureCollection',
        features: fleetHistoryData
          .filter((d) => d.v.reg === hoveredReg)
          .map((d) => ({
            type: 'Feature',
            properties: { reg: d.v.reg },
            geometry: {
              type: 'LineString',
              coordinates: (d.H?.pts || []).map((q) => q.p),
            },
          })),
      };
      map.getSource('hi').setData(hiData);
      map.setPaintProperty('routes', 'line-opacity', hoveredReg ? 0.35 : 0.9);
    }
  }, [hoveredReg, mode, focusedVehicle, fleetHistoryData, mapLoaded]);

  // ----------------------------------------------------
  // 3. HISTORY MODE - SINGLE FOCUSED VEHICLE PLAYBACK
  // ----------------------------------------------------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || mode !== 'history' || !focusedVehicle || !historyData) return;

    const maplibregl = window.maplibregl;
    const H = historyData.H;
    const segs = historyData.segs || [];
    const pts = H?.pts || [];
    // Clear previous layers & markers
    histMarkersRef.current.forEach((m) => m.remove());
    histMarkersRef.current = [];
    Object.keys(markersRef.current).forEach((reg) => {
      markersRef.current[reg].remove();
      delete markersRef.current[reg];
    });
    clusterMarkersRef.current.forEach((m) => m.remove());
    clusterMarkersRef.current = [];
    if (clusterPopupRef.current) {
      clusterPopupRef.current.remove();
      clusterPopupRef.current = null;
    }
    overlayPinsRef.current.forEach((m) => m.remove());
    overlayPinsRef.current = [];
    safeRemoveLayer(map, 'trip-casing');
    safeRemoveLayer(map, 'trip-left');
    safeRemoveLayer(map, 'trip-done');
    safeRemoveLayer(map, 'trip-last');
    safeRemoveLayer(map, 'routes');
    safeRemoveLayer(map, 'hi');
    safeRemoveLayer(map, 'track');
    safeRemoveLayer(map, 'offtrip');
    safeRemoveLayer(map, 'played');
    if (carMarkerRef.current) {
      carMarkerRef.current.remove();
      carMarkerRef.current = null;
    }

    if (!pts.length) {
      if (focusedVehicle.lng && focusedVehicle.lat) {
        map.flyTo({
          center: [Number(focusedVehicle.lng), Number(focusedVehicle.lat)],
          zoom: 13,
          duration: 800,
        });
      }
      return;
    }

    // 1. Full track line in light purple (use polylineCoords if available, else point coordinates)
    const trackCoords =
      historyData.H?.polylineCoords && historyData.H.polylineCoords.length >= 2
        ? historyData.H.polylineCoords
        : pts.map((q) => q.p);

    map.addSource('track', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: trackCoords },
      },
    });
    map.addLayer({
      id: 'track',
      type: 'line',
      source: 'track',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#D6C9F9', 'line-width': 5 },
    });

    // 2. Off-trip runs in pink
    const offRuns = (historyData.runs || [])
      .filter((r) => r.k === 'off')
      .map((r) => pts.slice(r.n0, r.n1 + 2).map((q) => q.p));
    map.addSource('offtrip', {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'MultiLineString', coordinates: offRuns } },
    });
    map.addLayer({
      id: 'offtrip',
      type: 'line',
      source: 'offtrip',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': OFF_HEX, 'line-width': 5, 'line-opacity': 0.85 },
    });

    // 3. Played line from start
    const playedCoords = [pts[0].p, pts[0].p];
    map.addSource('played', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: playedCoords.length ? playedCoords : [pts[0].p, pts[0].p],
        },
      },
    });
    map.addLayer({
      id: 'played',
      type: 'line',
      source: 'played',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#4014B8', 'line-width': 5 },
    });

    // Point Marker helper with rich tooltip popup
    const createPtMarker = (p, col, lab, title, popupHtml = null, isVio = false) => {
      if (!p || !Array.isArray(p) || p.length < 2 || isNaN(Number(p[0])) || isNaN(Number(p[1])) || (Number(p[0]) === 0 && Number(p[1]) === 0)) {
        return null;
      }

      const wrap = document.createElement('div');
      wrap.className = `pt-marker-wrap ${isVio ? 'vio-pt' : ''}`;

      const pin = document.createElement('div');
      pin.className = `pt-marker-pin ${isVio ? 'vio-pt' : ''}`;
      pin.style.backgroundColor = col;
      pin.style.color = '#fff';
      pin.style.display = 'grid';
      pin.style.placeItems = 'center';
      pin.style.fontWeight = '700';
      pin.style.fontSize = '9px';
      pin.style.width = '18px';
      pin.style.height = '18px';
      pin.style.borderRadius = '50%';
      pin.style.border = '2px solid #fff';
      pin.style.boxShadow = '0 1px 4px rgba(0,0,0,.35)';
      pin.style.cursor = 'pointer';
      pin.style.transition = 'transform 0.15s ease';
      pin.textContent = lab;

      wrap.appendChild(pin);

      if (title) wrap.title = title;

      // Transform inner pin only so MapLibre's marker translate(x, y) positioning on the map is preserved
      wrap.addEventListener('mouseenter', () => {
        pin.style.transform = 'scale(1.25)';
      });
      wrap.addEventListener('mouseleave', () => {
        pin.style.transform = 'scale(1)';
      });

      const m = new maplibregl.Marker({ element: wrap, anchor: 'center' }).setLngLat(p);
      if (popupHtml) {
        const popup = new maplibregl.Popup({ offset: 12, closeButton: true, className: 'vt-map-popup' })
          .setHTML(popupHtml);
        m.setPopup(popup);
      }
      m.addTo(map);
      histMarkersRef.current.push(m);
      return m;
    };

    // S (Start) and E (End) markers using exact backend API locations if available
    const startLoc =
      historyData.startPoint?.location ||
      pts[0]?.location ||
      placeName(pts[0].p);
    const startTime =
      historyData.startPoint?.time ||
      (pts[0]?.time ? pts[0].time : clock(pts[0].t));

    createPtMarker(
      pts[0].p,
      STATUS_HEX.moving,
      'S',
      `Start · ${startLoc} · ${startTime}`,
      `<div style="padding:4px 6px;font-family:sans-serif;min-width:180px;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
          <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${STATUS_HEX.moving};"></span>
          <b style="color:${STATUS_HEX.moving};font-size:12px;">Route Start</b>
        </div>
        <div style="font-size:11.5px;color:#5C5F80;margin-bottom:2px;">Time: <b>${startTime}</b> (${dt(pts[0].t)})</div>
        <div style="font-size:11.5px;color:#333;border-top:1px solid #EDEDF2;padding-top:4px;">📍 ${startLoc}</div>
      </div>`
    );

    const endLoc =
      historyData.endPoint?.location ||
      pts[pts.length - 1]?.location ||
      placeName(pts[pts.length - 1].p);
    const endTime =
      historyData.endPoint?.time ||
      (pts[pts.length - 1]?.time ? pts[pts.length - 1].time : clock(pts[pts.length - 1].t));

    createPtMarker(
      pts[pts.length - 1].p,
      '#000333',
      'E',
      `End · ${endLoc} · ${endTime}`,
      `<div style="padding:4px 6px;font-family:sans-serif;min-width:180px;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
          <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#000333;"></span>
          <b style="color:#000333;font-size:12px;">Route End / Position</b>
        </div>
        <div style="font-size:11.5px;color:#5C5F80;margin-bottom:2px;">Time: <b>${endTime}</b></div>
        <div style="font-size:11.5px;color:#333;border-top:1px solid #EDEDF2;padding-top:4px;">📍 ${endLoc}</div>
      </div>`
    );

    // Segments markers (Idle 'I', Stopped 'P')
    segs.forEach((g) => {
      if (g.st !== 'moving') {
        const isIdle = g.st === 'idle';
        const color = isIdle ? '#B26800' : '#D13B3B';
        const segPt = pts[g.n0]?.p || g.p0;
        if (!segPt) return;
        const segLoc = g.locationStart || pts[g.n0]?.location || placeName(segPt);
        const title = `${isIdle ? 'Idle' : 'Stopped'} · ${clock(g.t0)}–${clock(g.t1)} · ${dur(g.min)} · ${segLoc}`;
        const popupHtml = `
          <div style="padding:4px 6px;font-family:sans-serif;min-width:190px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};"></span>
              <b style="color:${color};font-size:12px;">${isIdle ? 'Idle (Ignition ON)' : 'Stopped (Ignition OFF)'}</b>
            </div>
            <div style="font-size:13px;font-weight:700;color:#10052E;margin-bottom:2px;">${dur(g.min)}</div>
            <div style="font-size:11.5px;color:#5C5F80;margin-bottom:4px;">${clock(g.t0)} – ${clock(g.t1)}</div>
            <div style="font-size:11.5px;color:#333;border-top:1px solid #EDEDF2;padding-top:4px;">📍 ${segLoc}</div>
            ${isIdle && FUEL_CONFIG.idleEnabled ? `<div style="font-size:11px;color:#8B8CA7;margin-top:2px;">Est. Idle Fuel: ${((FUEL_CONFIG.idleLph[focusedVehicle?.cat || 'HTV'] * g.min) / 60).toFixed(1)} L</div>` : ''}
          </div>
        `;
        createPtMarker(
          segPt,
          color,
          isIdle ? 'I' : 'P',
          title,
          popupHtml
        );
      }
    });

    // Trips A & B
    (historyData.trips || []).forEach((t) => {
      if (t.a) {
        createPtMarker(
          t.a,
          '#248413',
          'A',
          `Trip ${t.id} Origin · ${t.from}`,
          `<div style="padding:4px 6px;font-family:sans-serif;min-width:180px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#248413;"></span>
              <b style="color:#248413;font-size:12px;">Trip ${t.id} Origin</b>
            </div>
            <div style="font-size:12px;font-weight:600;color:#10052E;">${t.from}</div>
            <div style="font-size:11px;color:#5C5F80;margin-top:2px;">Departed: <b>${clock(t.t0)}</b></div>
          </div>`
        );
      }
      if (t.b) {
        createPtMarker(
          t.b,
          TRIP_HEX,
          'B',
          `Trip ${t.id} Destination · ${t.to}`,
          `<div style="padding:4px 6px;font-family:sans-serif;min-width:180px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${TRIP_HEX};"></span>
              <b style="color:${TRIP_HEX};font-size:12px;">Trip ${t.id} Destination</b>
            </div>
            <div style="font-size:12px;font-weight:600;color:#10052E;">${t.to}</div>
            <div style="font-size:11px;color:#5C5F80;margin-top:2px;">Status: <b>${t.st === 'done' ? 'Completed (' + clock(t.t1) + ')' : 'In-Transit'}</b></div>
          </div>`
        );
      }
    });

    // Violations
    (historyData.vioList || []).forEach((e) => {
      if (e.p) {
        createPtMarker(
          e.p,
          e.sev === 'high' ? '#CE1717' : '#B26800',
          '!',
          `${VIO[e.type]?.label || e.type} · ${vioValue(e)} · ${clock(e.t)}`,
          `<div style="padding:4px 6px;font-family:sans-serif;min-width:180px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${e.sev === 'high' ? '#CE1717' : '#B26800'};"></span>
              <b style="color:${e.sev === 'high' ? '#CE1717' : '#B26800'};font-size:12px;">${VIO[e.type]?.label || e.type}</b>
            </div>
            <div style="font-size:11.5px;color:#5C5F80;">Value: <b>${vioValue(e)}</b></div>
            <div style="font-size:11px;color:#8B8CA7;">Time: ${clock(e.t)}</div>
          </div>`,
          true
        );
      }
    });

    // Moving Car marker
    const curPt = pts[0];
    const carEl = document.createElement('div');
    const st = curPt.spd > 0 ? 'moving' : curPt.ign ? 'idle' : 'stopped';
    const col = STATUS_HEX[st] || '#5C5F80';
    let initH = Math.round(curPt.h || 0);
    if (initH === 0 && pts.length >= 2 && pts[1]?.p) {
      initH = Math.round(bearing(curPt.p, pts[1].p));
    }
    carEl.className = `vm ${focusedVehicle.cat} sel`;
    carEl.innerHTML = `
      <span class="tdv" style="transform: rotate(${initH}deg);">
        ${topDownSVG(focusedVehicle.cat, col)}
      </span>
      <span class="tag on">${focusedVehicle.reg}</span>
    `;

    if (carMarkerRef.current) carMarkerRef.current.remove();
    carMarkerRef.current = new maplibregl.Marker({ element: carEl })
      .setLngLat(curPt.p)
      .addTo(map);

    // Initial camera fit
    map.fitBounds(boundsOf(pts.map((q) => q.p)), {
      padding: 50,
      duration: 500,
      maxZoom: 14,
    });
  }, [mode, focusedVehicle, historyData, mapLoaded]);

  // Update playback position marker & played line dynamically
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || mode !== 'history' || !focusedVehicle || !historyData) return;

    const pts = historyData.H?.pts || [];
    if (!pts.length) return;
    const curPt = pts[playbackStep] || pts[0];
    if (!curPt || !curPt.p) return;

    // Smoothly pan camera to current playback/segment position
    if (map && curPt && curPt.p) {
      map.easeTo({ center: curPt.p, duration: 400 });
    }

    // Update car marker
    if (carMarkerRef.current) {
      carMarkerRef.current.setLngLat(curPt.p);
      const el = carMarkerRef.current.getElement();
      const st = curPt.spd > 0 ? 'moving' : curPt.ign ? 'idle' : 'stopped';
      const col = STATUS_HEX[st] || '#5C5F80';
      let stepH = Math.round(curPt.h || 0);
      if (stepH === 0 && pts.length >= 2) {
        if (playbackStep < pts.length - 1 && pts[playbackStep + 1]?.p) {
          stepH = Math.round(bearing(curPt.p, pts[playbackStep + 1].p));
        } else if (playbackStep > 0 && pts[playbackStep - 1]?.p) {
          stepH = Math.round(bearing(pts[playbackStep - 1].p, curPt.p));
        }
      }
      const tdv = el.querySelector('.tdv');
      if (tdv) {
        tdv.style.transform = `rotate(${stepH}deg)`;
        tdv.innerHTML = topDownSVG(focusedVehicle.cat, col);
      }
    }

    // Update played line
    if (map.getSource('played')) {
      const playedCoords = pts.slice(0, playbackStep + 1).map((q) => q.p);
      map.getSource('played').setData({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: playedCoords.length ? playedCoords : [pts[0].p, pts[0].p],
        },
      });
    }
  }, [playbackStep, mode, focusedVehicle, historyData, mapLoaded]);

  const historyPts = historyData?.H?.pts || [];
  const currentHistoryPt = historyPts[playbackStep] || historyPts[0];

  return (
    <div className="map-shell">
      <div ref={mapContainerRef} className="map" />

      {/* Floating Follow / Re-center Button for Selected Vehicle */}
      {mode === 'live' && selectedVehicle && (
        <button
          type="button"
          className={`map-follow-btn ${followVehicle ? 'following' : ''}`}
          onClick={() => onToggleFollow && onToggleFollow(!followVehicle)}
          title={
            followVehicle
              ? `Following ${selectedVehicle.reg} live (click to unlock map)`
              : `Center & follow ${selectedVehicle.reg}`
          }
        >
          {followVehicle ? (
            <>
              <span className="live-dot" />
              <span>Following {selectedVehicle.reg}</span>
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="12" cy="12" r="7" />
                <line x1="12" y1="2" x2="12" y2="5" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="1" y1="12" x2="5" y2="12" />
                <line x1="19" y1="12" x2="23" y2="12" />
              </svg>
              <span>Follow {selectedVehicle.reg}</span>
            </>
          )}
        </button>
      )}

      {/* Map Controls */}
      <button
        type="button"
        className={`map-only-btn ${mapOnly ? 'active' : ''}`}
        title={mapOnly ? 'Exit map view' : 'Map only (fullscreen)'}
        onClick={onToggleMapOnly}
        dangerouslySetInnerHTML={{
          __html: mapOnly
            ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>'
            : '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
        }}
      />



      {mode === 'live' && (
        <button
          type="button"
          className="map-group-btn"
          title={
            groupedMarkers
              ? 'Show every vehicle individually (no grouping)'
              : 'Group nearby vehicles into count circles'
          }
          aria-label={groupedMarkers ? 'Show individual vehicles' : 'Group nearby vehicles'}
          aria-pressed={!groupedMarkers}
          onClick={handleToggleGrouping}
          dangerouslySetInnerHTML={{
            __html: groupedMarkers
              ? '<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" stroke="none"><circle cx="5" cy="6" r="2"/><circle cx="18" cy="5" r="2"/><circle cx="11" cy="12" r="2"/><circle cx="5" cy="18" r="2"/><circle cx="18" cy="17" r="2"/></svg>'
              : '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="8.5"/><circle cx="9" cy="10" r="1.3" fill="currentColor" stroke="none"/><circle cx="15" cy="10" r="1.3" fill="currentColor" stroke="none"/><circle cx="12" cy="15" r="1.3" fill="currentColor" stroke="none"/></svg>',
          }}
        />
      )}

      {toastInfo && (
        <div className="map-toast-wrap">
          <div className="map-toast">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <div
              className="map-toast-msg"
              dangerouslySetInnerHTML={{ __html: toastInfo.message }}
            />
            <button
              type="button"
              className="x"
              aria-label="Dismiss"
              onClick={() => setToastInfo(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* History Playback Controls (Single Vehicle Focus) */}
      {mode === 'history' && focusedVehicle && historyPts.length > 0 && (
        <div className="hv-play">
          <button
            type="button"
            className="btn primary sm"
            id="pp"
            aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={onTogglePlay}
            dangerouslySetInnerHTML={{
              __html: isPlaying
                ? ic('pause', 'width="14" height="14"')
                : ic('play', 'width="14" height="14"'),
            }}
          />

          <div className="segbar sm" id="pspeed">
            {[1, 4, 8].map((s) => (
              <button
                key={s}
                type="button"
                className={playbackSpeed === s ? 'on' : ''}
                onClick={() => onChangePlaybackSpeed && onChangePlaybackSpeed(s)}
              >
                {s}×
              </button>
            ))}
          </div>

          <input
            type="range"
            id="pscrub"
            min="0"
            max={historyPts.length - 1}
            value={playbackStep}
            aria-label="Playback position"
            onChange={(e) => onSeekPlayback && onSeekPlayback(+e.target.value)}
          />

          <span className="num hv-time" id="ptime">
            {currentHistoryPt ? dt(currentHistoryPt.t) : ''}
          </span>

          <span
            className="info-tip"
            title="Press play to replay route; choose 1×, 4× or 8×; drag slider to jump to a time."
          >
            i
          </span>
        </div>
      )}

      {/* Live / Fleet History Legend */}
      {(mode === 'live' || (mode === 'history' && !focusedVehicle)) && (
        <div className="map-legend">
          {['moving', 'idle', 'stopped', 'inactive'].map((st) => (
            <span key={st} className="li">
              <span className="sw" style={{ background: STATUS_HEX[st] }} />
              {STATUS_LABEL[st]}
            </span>
          ))}
          <span className="li" style={{ marginTop: '3px' }}>
            <span
              className="lic-td"
              dangerouslySetInnerHTML={{ __html: topDownSVG('LTV', '#5C5F80') }}
            />
            LTV
            <span
              className="lic-td"
              style={{ marginLeft: '8px' }}
              dangerouslySetInnerHTML={{ __html: topDownSVG('HTV', '#5C5F80') }}
            />
            HTV
          </span>
        </div>
      )}
    </div>
  );
};

export default VehicleTrackingMap;
